<template>
	<div
		v-if="shouldShowChat"
		class="floating-chat"
		:class="{ 'floating-chat--open': isOpen }"
	>
		<transition name="chat-fade">
			<v-card
				v-if="isOpen"
				class="chat-container"
				elevation="10"
			>
				<header class="chat-header">
					<!-- Botão para abrir conversas no mobile - fixo no topo à esquerda -->
					<v-btn
						v-if="isMobile"
						icon
						small
						class="chat-mobile-conversations-toggle"
						@click="showMobileConversations = !showMobileConversations"
					>
						<v-icon>mdi-menu</v-icon>
					</v-btn>
					<div class="chat-header__title">
						<span class="chat-header__label">Assistente virtual</span>
						<h3 class="chat-header__headline">Fale com o Mévio</h3>
					</div>
					<div class="chat-header__actions">
						<v-btn
							icon
							small
							@click="loadConversations"
							:title="refreshTitle"
							:loading="loadingConversations"
						>
							<v-icon>mdi-refresh</v-icon>
						</v-btn>
						<v-btn
							icon
							small
							@click="handleClose"
							:title="closeButtonTitle"
						>
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</div>
				</header>

				<v-divider></v-divider>

				<section class="chat-body">
					<!-- Overlay para mobile quando conversas estão abertas -->
					<div
						v-if="isMobile && showMobileConversations"
						class="chat-mobile-overlay"
						@click="showMobileConversations = false"
					></div>

					<aside 
						class="chat-conversations"
						:class="{ 'chat-conversations--mobile-open': isMobile && showMobileConversations }"
						:style="!isMobile ? { width: conversationsWidth + 'px', minWidth: conversationsWidth + 'px', maxWidth: conversationsWidth + 'px' } : {}"
					>
						<div class="chat-conversations__header">
							<h4 class="chat-conversations__title">Conversas</h4>
							<div class="chat-conversations__header-actions">
								<v-btn
									text
									small
									class="chat-conversations__new"
									@click="createNewConversation"
								>
									<v-icon
										left
										small
										>mdi-plus</v-icon
									>
									<span class="chat-conversations__new-text">Nova conversa</span>
								</v-btn>
								<!-- Botão fechar no mobile -->
								<v-btn
									v-if="isMobile"
									icon
									small
									class="chat-conversations__close-mobile"
									@click="showMobileConversations = false"
								>
									<v-icon>mdi-close</v-icon>
								</v-btn>
							</div>
						</div>

						<div class="chat-conversations__list">
							<template v-if="loadingConversations">
								<div
									v-for="index in 3"
									:key="`conv-loading-${index}`"
									class="conversation-item conversation-item--loading"
								>
									<v-skeleton-loader
										type="avatar, text"
										class="mb-2"
									/>
								</div>
							</template>

							<template v-else-if="conversationList.length">
								<v-list
									dense
									nav
									class="chat-conversations__items"
								>
									<v-list-item
										v-for="conversation in conversationList"
										:key="getConversationId(conversation)"
										:class="conversationClasses(conversation)"
										@click="selectConversation(conversation)"
									>
										<v-list-item-avatar
											class="conversation-avatar"
											size="44"
										>
											<span>{{ conversationAvatar(conversation) }}</span>
										</v-list-item-avatar>
										<v-list-item-content style="z-index: 99999999">
											<v-tooltip bottom>
												<template v-slot:activator="{ on, attrs }">
													<v-list-item-title v-bind="attrs" v-on="on">
														{{ conversationTitle(conversation) }}
													</v-list-item-title>
												</template>
												<span>{{ conversationTitle(conversation) }}</span>
											</v-tooltip>
											<v-list-item-subtitle class="conversation-subtitle-row">
												<span class="conversation-subtitle-text">
													{{ conversationSubtitle(conversation) }}
												</span>
												<span class="conversation-last-activity">
													{{ conversationLastActivity(conversation) }}
												</span>
											</v-list-item-subtitle>
										</v-list-item-content>
										<v-list-item-action class="conversation-item__action">
											<v-btn
												icon
												small
												@click.stop="deleteConversation(conversation)"
												color="error"
												class="conversation-delete-btn"
											>
												<v-icon small>mdi-delete</v-icon>
											</v-btn>
										</v-list-item-action>
										<v-badge
											v-if="conversationUnread(conversation)"
											color="#F15A24"
											dot
										></v-badge>
									</v-list-item>
								</v-list>
							</template>

							<div
								v-else
								class="chat-conversations__empty"
							>
								<v-icon color="#F15A24">mdi-chat-plus-outline</v-icon>
								<p>Inicie uma conversa para começar o atendimento.</p>
							</div>
						</div>
					</aside>

					<!-- Divisor redimensionável (apenas desktop) -->
					<div
						v-if="!isMobile"
						class="chat-resizer"
						@mousedown="startResize"
					></div>

					<main class="chat-thread">
						<header
							class="chat-thread__header"
							v-if="activeConversation"
						>
							<div>
								<h4>{{ conversationTitle(activeConversation) }}</h4>
								<small>{{ conversationSubtitle(activeConversation) }}</small>
							</div>
						</header>

						<div
							v-if="!activeConversation && !loadingConversations"
							class="chat-thread__placeholder"
						>
							<v-icon
								color="#F15A24"
								class="mb-3"
								>mdi-chat-processing-outline</v-icon
							>
							<p>Selecione uma conversa ou crie um novo atendimento.</p>
						</div>

						<div
							class="chat-thread__messages"
							ref="messagesContainer"
						>
							<template v-if="loadingMessages && !waitingAssistantResponse">
								<div
									v-for="index in 3"
									:key="`msg-loading-${index}`"
									class="message-bubble message-bubble--loading"
								>
									<v-skeleton-loader
										type="text"
										class="mb-2"
									/>
									<v-skeleton-loader
										type="text"
										width="60%"
									/>
								</div>
							</template>

							<template v-else-if="currentMessages.length">
								<div
									v-for="message in currentMessages"
									:key="message.id || message.timestamp"
									:class="messageClasses(message)"
								>
									<div class="message-bubble__content">
										<p
											class="message-bubble__text"
											v-html="formatMessageText(message)"
										></p>
										<!-- Exibir arquivos anexados - COMENTADO -->
										<!-- <div
											v-if="message.arquivos && message.arquivos.length > 0"
											class="message-bubble__attachments"
										>
											<div
												v-for="(arquivo, idx) in message.arquivos"
												:key="idx"
												class="message-attachment"
											>
												<v-icon
													small
													class="message-attachment__icon"
													>{{ getFileIcon(arquivo.type || arquivo.filename_download) }}</v-icon
												>
												<a
													:href="getFileUrl(arquivo)"
													target="_blank"
													class="message-attachment__link"
													>{{ arquivo.filename_download || arquivo.filename || 'Arquivo' }}</a
												>
											</div>
										</div> -->
										<!-- Botões quando a resposta cita lei/artigo -->
										<div
											v-if="
												message.id_lei && message.enviado_por === 'assistant'
											"
											class="message-bubble__law-actions mt-2"
										>
											<v-btn
												small
												outlined
												color="#f15a24"
												class="message-bubble__law-button"
												@click="goToLaw(message.id_lei, message.numero_lei, message.artigo_ref)"
											>
												<v-icon
													small
													left
													>mdi-book-open-variant</v-icon
												>
												Ir até a lei
											</v-btn>
											<v-btn
												v-if="hasSingleCitedArticle(message)"
												small
												outlined
												color="#f15a24"
												class="message-bubble__law-button ml-2"
												:loading="creatingAnnotationId === (message.id || message.timestamp)"
												:disabled="creatingAnnotationId === (message.id || message.timestamp)"
												@click="createAnnotationFromMessage(message)"
											>
												<v-icon
													small
													left
													>mdi-note-plus-outline</v-icon
												>
												Criar uma anotação
											</v-btn>
										</div>
										<div class="message-bubble__footer">
											<span class="message-bubble__time">{{
												formatTimestamp(message)
											}}</span>
											<!-- Menu de opções (apenas respostas da IA) -->
											<v-menu
												v-if="message.enviado_por === 'assistant'"
												:close-on-content-click="true"
												offset-y
												left
												content-class="message-bubble-menu"
												transition="scale-transition"
											>
												<template v-slot:activator="{ on, attrs }">
													<v-btn
														icon
														x-small
														class="message-bubble__menu-btn"
														v-bind="attrs"
														v-on="on"
														@click.stop
													>
														<v-icon small>mdi-dots-vertical</v-icon>
													</v-btn>
												</template>
												<v-list dense>
													<v-list-item
														@click="generateDocumentFromMessage(message)"
														:disabled="generatingDocumentId === (message.id || message.timestamp)"
													>
														<v-list-item-icon>
															<v-progress-circular
																v-if="generatingDocumentId === (message.id || message.timestamp)"
																indeterminate
																size="20"
																width="2"
															></v-progress-circular>
															<v-icon v-else>mdi-file-document-outline</v-icon>
														</v-list-item-icon>
														<v-list-item-title>Gerar documento</v-list-item-title>
													</v-list-item>
												</v-list>
											</v-menu>
										</div>
									</div>
								</div>
								<!-- Animação de "digitando..." enquanto aguarda resposta do assistant -->
								<div
									v-if="waitingAssistantResponse"
									class="message-bubble message-bubble--typing"
								>
									<div class="message-bubble__content">
										<div class="typing-indicator">
											<span></span>
											<span></span>
											<span></span>
										</div>
									</div>
								</div>
							</template>

							<div
								v-else-if="activeConversation && !waitingAssistantResponse"
								class="chat-thread__empty"
							>
								<v-icon
									color="#F15A24"
									class="mb-3"
									>mdi-forum-outline</v-icon
								>
								<p>
									Ainda não há mensagens aqui. Envie a primeira para começar.
								</p>
							</div>
							<!-- Animação de "digitando..." quando não há mensagens ainda -->
							<div
								v-if="
									activeConversation &&
									waitingAssistantResponse &&
									!currentMessages.length
								"
								class="message-bubble message-bubble--typing"
							>
								<div class="message-bubble__content">
									<div class="typing-indicator">
										<span></span>
										<span></span>
										<span></span>
									</div>
								</div>
							</div>
						</div>

						<footer class="chat-thread__footer">
							<!-- Preview de arquivos anexados - COMENTADO -->
							<!-- <div
								v-if="attachedFiles.length > 0"
								class="chat-thread__attachments-preview"
							>
								<div class="chat-thread__attachments-header">
									<v-icon
										small
										color="#F15A24"
										class="mr-2"
										>mdi-paperclip</v-icon
									>
									<span class="chat-thread__attachments-title">
										{{ attachedFiles.length }} arquivo{{ attachedFiles.length > 1 ? 's' : '' }} anexado{{ attachedFiles.length > 1 ? 's' : '' }}
									</span>
								</div>
								<div class="chat-thread__attachments-list">
									<div
										v-for="(file, index) in attachedFiles"
										:key="index"
										class="attachment-preview"
									>
										<v-icon
											small
											class="attachment-preview__icon"
											>{{ getFileIcon(file.name) }}</v-icon
										>
										<span class="attachment-preview__name">{{ file.name }}</span>
										<v-btn
											icon
											x-small
											class="attachment-preview__remove"
											@click="removeAttachment(index)"
										>
											<v-icon x-small>mdi-close</v-icon>
										</v-btn>
									</div>
								</div>
							</div> -->
							<div class="chat-thread__input-wrapper">
								<!-- Input de arquivo oculto - COMENTADO -->
								<!-- <input
									ref="fileInput"
									type="file"
									multiple
									style="display: none"
									@change="handleFileSelect"
								/> -->
								<!-- Botão de anexo - COMENTADO -->
								<!-- <v-btn
									icon
									large
									color="#F15A24"
									class="chat-thread__attach"
									:class="{ 'chat-thread__attach--active': attachedFiles.length > 0 }"
									:disabled="!activeConversation"
									@click="$refs.fileInput.click()"
									:title="attachedFiles.length > 0 ? `${attachedFiles.length} arquivo(s) anexado(s)` : 'Anexar arquivo'"
								>
									<v-icon>mdi-paperclip</v-icon>
									<v-badge
										v-if="attachedFiles.length > 0"
										:content="attachedFiles.length"
										color="#F15A24"
										overlap
										class="chat-thread__attach-badge"
									>
									</v-badge>
								</v-btn> -->
								<v-textarea
									ref="messageInput"
									v-model="messageDraft"
									outlined
									dense
									hide-details
									color="#F15A24"
									label="Digite sua mensagem"
									:disabled="!activeConversation"
									class="chat-thread__input"
									rows="1"
									auto-grow
									@keydown.enter.exact="handleEnterKey"
									@focus="handleInputFocus"
								></v-textarea>
								<v-btn
									icon
									large
									color="#F15A24"
									class="chat-thread__send"
									:disabled="isSendDisabled"
									@click="submitMessage"
								>
									<v-icon>mdi-send</v-icon>
								</v-btn>
							</div>
						</footer>
					</main>
				</section>
			</v-card>
		</transition>

		<v-fab-transition>
			<v-tooltip
				top
				:value="showMeioTooltip && !isOpen"
				:open-on-hover="false"
				content-class="floating-chat-meio-tooltip"
				transition="meio-tooltip-transition"
				:open-delay="0"
				:offset-y="10"
			>
				<template v-slot:activator="{ attrs }">
					<v-btn
						v-bind="attrs"
						@click="handleFloatingButtonClick"
						color="#F15A24"
						dark
						fab
						large
						class="floating-button"
						:title="buttonTitle"
					>
						<v-icon v-if="!isOpen">mdi-message-text-outline</v-icon>
						<v-icon v-else>mdi-close</v-icon>
					</v-btn>
				</template>
				<div class="meio-tooltip__content">
					<v-icon small class="meio-tooltip__icon">mdi-robot-outline</v-icon>
					<span class="meio-tooltip__text">Fale com o Mévio, seu agente de IA jurídico</span>
				</div>
			</v-tooltip>
		</v-fab-transition>

		<v-snackbar
			v-model="snackbarChat"
			:color="snackbarChatColor"
			:timeout="4000"
			top
		>
			{{ snackbarChatText }}
			<template v-slot:action="{ attrs }">
				<v-btn text v-bind="attrs" @click="snackbarChat = false">
					Fechar
				</v-btn>
			</template>
		</v-snackbar>
	</div>
</template>

<script>
	export default {
		name: 'FloatingChat',
		data() {
			// Carregar largura salva do localStorage ou usar padrão
			const savedWidth = localStorage.getItem('chat-conversations-width');
			const defaultWidth = 320; // Aumentado de 280 para 320
			const conversationsWidth = savedWidth ? parseInt(savedWidth, 10) : defaultWidth;
			
			return {
				isOpen: false,
				messageDraft: '',
				selectedConversationId: null,
				conversations: [],
				messages: [],
				loadingConversations: false,
				loadingMessages: false,
				waitingAssistantResponse: false,
				showMobileConversations: false,
				conversationsWidth: conversationsWidth,
				isResizing: false,
				startX: 0,
				startWidth: conversationsWidth,
				showMeioTooltip: false,
				meioTooltipTimeout: null,
				generatingDocumentId: null,
				creatingAnnotationId: null,
				snackbarChat: false,
				snackbarChatText: '',
				snackbarChatColor: 'success',
				// attachedFiles: [],
				// uploadingFiles: false,
			};
		},
		computed: {
			shouldShowChat() {
				const isLoggedIn =
					this.$store.getters.isLoggedIn ||
					(this.$store.state.user && this.$store.state.user.id);
				const isInPainel = this.$route.path.startsWith('/painel');
				return isLoggedIn && isInPainel;
			},
			buttonTitle() {
				return this.isOpen ? 'Fechar Mévio' : 'Fale com o Mévio';
			},
			closeButtonTitle() {
				return 'Fechar chat';
			},
			refreshTitle() {
				return 'Atualizar conversas';
			},
			conversationList() {
				return this.conversations;
			},
			activeConversation() {
				if (!this.selectedConversationId) {
					return null;
				}
				return (
					this.conversationList.find(
						(conversation) =>
							this.getConversationId(conversation) ===
							this.selectedConversationId
					) || null
				);
			},
			currentMessages() {
				if (!this.selectedConversationId) {
					return [];
				}
				const filtered = this.messages.filter(
					(msg) => msg.conversa === this.selectedConversationId
				);
				return filtered.sort((a, b) => {
					const sortA = a.sort || 0;
					const sortB = b.sort || 0;
					return sortA - sortB;
				});
			},
			isSendDisabled() {
				return (
					!this.messageDraft.trim() ||
					!this.activeConversation ||
					this.loadingMessages
					// || this.uploadingFiles
				);
			},
			isMobile() {
				return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
			},
		},
		watch: {
			shouldShowChat(value) {
				if (value) {
					this.loadConversations();
					// Tooltip do Mévio: agendar quando o chat ficar visível (ex.: após login e ir para o painel)
					console.log('[Mévio tooltip] shouldShowChat passou a true, agendando tooltip em 800ms');
					setTimeout(() => this.scheduleMeioTooltip(), 800);
				}
			},
			isOpen(value) {
				if (value) {
					this.dismissMeioTooltip();
					this.ensureSelectedConversation();
				}
			},
			conversationList: {
				handler() {
					this.ensureSelectedConversation();
				},
				immediate: true,
			},
			currentMessages: {
				handler() {
					this.$nextTick(() => this.scrollThreadToBottom());
				},
				deep: true,
			},
			isMobile(value) {
				// Fechar drawer quando sair do mobile
				if (!value) {
					this.showMobileConversations = false;
				}
			},
		},
		methods: {
			/** Chave do localStorage por usuário: cada conta vê o tooltip uma vez e só de novo após 10 dias. */
			meioTooltipStorageKey() {
				const userId = this.$store.state.user && this.$store.state.user.id;
				return userId ? `floating-chat-tooltip-last-shown-${userId}` : 'floating-chat-tooltip-last-shown';
			},
			/** Verifica se o tooltip do Mévio deve ser exibido (nunca viu ou passaram 10 dias). */
			shouldShowMeioTooltip() {
				const KEY = this.meioTooltipStorageKey();
				const TEN_DAYS_MS = 10 * 24 * 60 * 60 * 1000;
				try {
					const raw = localStorage.getItem(KEY);
					const result = !raw ? true : (Date.now() - new Date(raw).getTime() >= TEN_DAYS_MS);
					console.log('[Mévio tooltip] shouldShowMeioTooltip:', { key: KEY, raw, result });
					return result;
				} catch (e) {
					console.log('[Mévio tooltip] shouldShowMeioTooltip erro:', e);
					return true;
				}
			},
			/** Esconde o tooltip e grava a data para só reaparecer em 10 dias (por usuário). */
			dismissMeioTooltip() {
				this.showMeioTooltip = false;
				if (this.meioTooltipTimeout) {
					clearTimeout(this.meioTooltipTimeout);
					this.meioTooltipTimeout = null;
				}
				try {
					localStorage.setItem(this.meioTooltipStorageKey(), new Date().toISOString());
				} catch (_) {}
			},
			/** Abre o tooltip automaticamente uma vez e agenda fechamento em 5s. Não exibe se o chat já estiver aberto. */
			scheduleMeioTooltip() {
				const chatVisible = this.shouldShowChat;
				const shouldShow = this.shouldShowMeioTooltip();
				console.log('[Mévio tooltip] scheduleMeioTooltip:', { chatVisible, shouldShow, isOpen: this.isOpen, showMeioTooltip: this.showMeioTooltip });
				if (!chatVisible || !shouldShow || this.isOpen) return;
				this.showMeioTooltip = true;
				console.log('[Mévio tooltip] tooltip ativado (showMeioTooltip = true)');
				this.meioTooltipTimeout = setTimeout(() => {
					this.dismissMeioTooltip();
				}, 5000);
			},
			handleFloatingButtonClick(e) {
				this.dismissMeioTooltip();
				this.toggleChat();
			},
			toggleChat() {
				this.isOpen = !this.isOpen;
				this.emitState();
				if (this.isOpen) {
					this.ensureSelectedConversation();
				}
			},
			handleClose() {
				this.isOpen = false;
				this.emitState();
			},
			emitState() {
				this.$emit(this.isOpen ? 'open' : 'close');
			},
			ensureSelectedConversation() {
				if (!this.conversationList.length) {
					this.selectedConversationId = null;
					return;
				}
				if (!this.selectedConversationId) {
					this.selectedConversationId = this.getConversationId(
						this.conversationList[0]
					);
				}
			},
			async selectConversation(conversation) {
				const conversationId = this.getConversationId(conversation);
				if (!conversationId) {
					return;
				}
				this.selectedConversationId = conversationId;
				// Fechar drawer de conversas no mobile após selecionar
				if (this.isMobile) {
					this.showMobileConversations = false;
				}
				await this.loadMessages(conversationId);
			},
			async submitMessage() {
				if (this.isSendDisabled) {
					return;
				}

				const messageText = this.messageDraft.trim();
				if (!messageText) {
					return;
				}

				// Upload de arquivos primeiro - COMENTADO
				// let uploadedFileIds = [];
				// if (this.attachedFiles.length > 0) {
				// 	this.uploadingFiles = true;
				// 	try {
				// 		for (const file of this.attachedFiles) {
				// 			const uploadedFile = await this.uploadFile(file);
				// 			if (uploadedFile && uploadedFile.id) {
				// 				uploadedFileIds.push(uploadedFile.id);
				// 			}
				// 		}
				// 	} catch (error) {
				// 		window.console.error('Erro ao fazer upload dos arquivos:', error);
				// 		alert('Erro ao anexar arquivos. Tente novamente.');
				// 		this.uploadingFiles = false;
				// 		return;
				// 	}
				// 	this.uploadingFiles = false;
				// }

				this.messageDraft = '';
				// const filesToClear = [...this.attachedFiles];
				// this.attachedFiles = [];

				try {
					this.loadingMessages = true;
					this.waitingAssistantResponse = true;

					let conversationId = this.selectedConversationId;
					let isNewConversation = false;

					if (!conversationId) {
						const userId = this.$store.state.user && this.$store.state.user.id;
						if (!userId) {
							return;
						}

						const { data: newConversationData } = await this.$http.post(
							'/items/conversas',
							{
								titulo: 'Nova conversa',
								usuario: userId,
								status: 'published',
							}
						);

						if (newConversationData.data && newConversationData.data.id) {
							conversationId = newConversationData.data.id;
							this.selectedConversationId = conversationId;
							this.conversations.unshift(newConversationData.data);
							isNewConversation = true;
						} else {
							throw new Error('Erro ao criar conversa');
						}
					} else {
						const conversationMessages = this.messages.filter(
							(msg) => msg.conversa === conversationId
						);
						isNewConversation = conversationMessages.length === 0;
					}

					const conversationMessages = this.messages.filter(
						(msg) => msg.conversa === conversationId
					);
					const maxSort =
						conversationMessages.length > 0
							? Math.max(...conversationMessages.map((msg) => msg.sort || 0))
							: 0;

					const userMessage = {
						id: `temp-user-${Date.now()}`,
						texto: messageText || '',
						enviado_por: 'user',
						conversa: conversationId,
						sort: maxSort + 1,
						date_created: new Date().toISOString(),
						user_created: this.$store.state.user && this.$store.state.user.id,
						// arquivos: uploadedFileIds.length > 0 ? uploadedFileIds.map(id => ({ id })) : [],
					};
					this.messages.push(userMessage);

					this.$nextTick(() => this.scrollThreadToBottom());

					// Salvar mensagem do usuário no backend
					try {
						const messageData = {
							texto: messageText || '',
							enviado_por: 'user',
							conversa: conversationId,
							sort: maxSort + 1,
							user_created: this.$store.state.user && this.$store.state.user.id,
						};

						// Adicionar arquivos se houver - COMENTADO
						// if (uploadedFileIds.length > 0) {
						// 	messageData.arquivos = {
						// 		create: uploadedFileIds.map(id => ({
						// 			directus_files_id: { id }
						// 		}))
						// 	};
						// }

						const { data: savedMessage } = await this.$http.post(
							'/items/mensagens',
							messageData
						);

						// Atualizar mensagem temporária com dados do servidor
						if (savedMessage.data && savedMessage.data.id) {
							const messageIndex = this.messages.findIndex(
								(msg) => msg.id === userMessage.id
							);
							if (messageIndex !== -1) {
								this.$set(this.messages, messageIndex, {
									...savedMessage.data,
									arquivos: uploadedFileIds.length > 0 
										? uploadedFileIds.map(id => ({ id })) 
										: [],
								});
							}
						}
					} catch (error) {
						window.console.error('Erro ao salvar mensagem:', error);
					}

					const assistantPayload = {
						prompt: messageText,
						conversation_id: conversationId,
					};

					if (isNewConversation) {
						assistantPayload.new = true;
					}

					// Incluir arquivos no payload se houver - COMENTADO
					// if (uploadedFileIds.length > 0) {
					// 	assistantPayload.arquivos = uploadedFileIds;
					// }

					const { data: assistantResponse } = await this.$http.post(
						'/assistant',
						assistantPayload
					);

					const aiMessage = {
						id: `temp-ai-${Date.now()}`,
						texto: assistantResponse.response || '',
						enviado_por: 'assistant',
						conversa: conversationId,
						sort: maxSort + 2,
						date_created: new Date().toISOString(),
					};
					this.messages.push(aiMessage);

					if (assistantResponse.title) {
						await this.updateConversationTitleWithAnimation(
							conversationId,
							assistantResponse.title
						);
					}

					if (
						assistantResponse.type === 'law_reference' &&
						assistantResponse.lawName
					) {
						try {
							const { data: leisData } = await this.$http.get(
								`/items/leis?fields=id_lei,numero_lei&search=${encodeURIComponent(
									assistantResponse.lawName
								)}&limit=1`
							);

							if (leisData.data && leisData.data.length > 0) {
								const lei = leisData.data[0];
								const messageIndex = this.messages.findIndex(
									(msg) => msg.id === aiMessage.id
								);
								if (messageIndex !== -1) {
									this.$set(this.messages[messageIndex], 'id_lei', lei.id_lei);
									this.$set(this.messages[messageIndex], 'numero_lei', lei.numero_lei);
									const artigoRef = assistantResponse.articleNumber != null
										? Number(assistantResponse.articleNumber)
										: this.extractFirstArticleNumber(aiMessage.texto);
									if (artigoRef != null && !Number.isNaN(artigoRef)) {
										this.$set(this.messages[messageIndex], 'artigo_ref', artigoRef);
									}
								}
							}
						} catch (error) {
							window.console.error('Erro ao buscar lei:', error);
						}
					}

					this.$nextTick(() => this.scrollThreadToBottom());
				} catch (error) {
					window.console.error('Erro ao enviar mensagem:', error);
				} finally {
					this.loadingMessages = false;
					this.waitingAssistantResponse = false;
				}
			},
			getConversationId(conversation) {
				if (!conversation) {
					return null;
				}
				return (
					conversation.id ||
					conversation.conversation_id ||
					conversation.uuid ||
					conversation.identifier ||
					null
				);
			},
			conversationTitle(conversation) {
				if (!conversation) {
					return 'Conversa';
				}
				return (
					conversation.titulo ||
					conversation.title ||
					conversation.name ||
					conversation.label ||
					'Nova conversa'
				);
			},
			conversationSubtitle(conversation) {
				if (!conversation) {
					return '';
				}

				const conversationId = this.getConversationId(conversation);
				const lastMessage = this.messages
					.filter((msg) => msg.conversa === conversationId)
					.sort(
						(a, b) => new Date(b.date_created) - new Date(a.date_created)
					)[0];

				if (lastMessage && lastMessage.texto) {
					const preview =
						lastMessage.texto.length > 50
							? lastMessage.texto.substring(0, 50) + '...'
							: lastMessage.texto;
					return preview;
				}

				return conversation.subtitle || conversation.description || '';
			},
			conversationLastActivity(conversation) {
				if (!conversation) {
					return '';
				}

				const conversationId = this.getConversationId(conversation);
				const lastMessage = this.messages
					.filter((msg) => msg.conversa === conversationId)
					.sort(
						(a, b) => new Date(b.date_created) - new Date(a.date_created)
					)[0];

				if (lastMessage && lastMessage.date_created) {
					return this.formatRelativeTime(lastMessage.date_created);
				}

				if (conversation.date_updated) {
					return this.formatRelativeTime(conversation.date_updated);
				}

				if (conversation.date_created) {
					return this.formatRelativeTime(conversation.date_created);
				}

				return '';
			},
			conversationUnread() {
				return 0;
			},
			conversationAvatar(conversation) {
				const title = this.conversationTitle(conversation);
				if (!title) {
					return 'L';
				}
				return title.slice(0, 1).toUpperCase();
			},
			conversationClasses(conversation) {
				return {
					'conversation-item': true,
					'conversation-item--active':
						this.getConversationId(conversation) ===
						this.selectedConversationId,
				};
			},
			messageClasses(message) {
				return {
					'message-bubble': true,
					'message-bubble--outgoing': this.isOutgoingMessage(message),
				};
			},
			isOutgoingMessage(message) {
				if (!message) {
					return false;
				}

				return message.enviado_por === 'user';
			},
			formatTimestamp(message) {
				if (!message) {
					return '';
				}

				if (message.timestamp) {
					return message.timestamp;
				}

				if (message.date_created) {
					return this.formatTime(message.date_created);
				}

				return '';
			},
			formatMessageText(message) {
				if (!message) {
					return '';
				}
				const text = message.texto || message.text || message.content || '';
				return this.convertUrlsToLinks(text);
			},
			convertUrlsToLinks(text) {
				const URLMatcher =
					/(?:(?:https?|ftp|file):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[A-Z0-9+&@#/%=~_|$])/gim;
				return text.replace(
					URLMatcher,
					(found) =>
						`<a class="message-link" rel="noreferrer" target="_blank" href="${found}">${found}</a>`
				);
			},
			scrollThreadToBottom() {
				const container = this.$refs.messagesContainer;
				if (!container) {
					return;
				}
				container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
			},
			sanitizeTitle(title) {
				if (!title) return '';
				var slug = '';
				var titleLower = title.toLowerCase();
				slug = titleLower.replace(/e|é|è|ẽ|ẻ|ẹ|ê|ế|ề|ễ|ể|ệ/gi, 'e');
				slug = slug.replace(/a|á|à|ã|ả|ạ|ă|ắ|ằ|ẵ|ẳ|ặ|â|ấ|ầ|ẫ|ẩ|ậ/gi, 'a');
				slug = slug.replace(/o|ó|ò|õ|ỏ|ọ|ô|ố|ồ|ỗ|ổ|ộ|ơ|ớ|ờ|ỡ|ở|ợ/gi, 'o');
				slug = slug.replace(/u|ú|ù|ũ|ủ|ụ|ư|ứ|ừ|ữ|ử|ự/gi, 'u');
				slug = slug.replace(/đ/gi, 'd');
				slug = slug.replace(/\s*$/g, '');
				slug = slug.replace(/\s+/g, '-');

				return slug;
			},
			/** Extrai o primeiro número de artigo citado no texto (ex.: "artigo 422", "Art. 422", "artigos 129 a 145" → 422 ou 129). */
			extractFirstArticleNumber(text) {
				if (!text || typeof text !== 'string') return null;
				const stripped = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
				// Art. 422, artigo 422, art. 422, artigos 129 a 145
				const match = stripped.match(/(?:artigos?|art\.?)\s*(\d+)/i);
				return match ? parseInt(match[1], 10) : null;
			},
			/**
			 * Extrai todos os números de artigo citados no texto (ex.: "artigos 186 e 927" → [186, 927]).
			 * Usado para só exibir "Criar uma anotação" quando houver exatamente um artigo citado.
			 */
			extractAllCitedArticleNumbers(text) {
				if (!text || typeof text !== 'string') return [];
				const stripped = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
				const numbers = [];
				// Padrões: "artigo 18", "art. 422", "artigos 186 e 927", "artigo 216-A", "artigos 129 a 145"
				const mainRegex = /(?:artigos?|art\.?)\s*(\d+)(?:\s*[-a-zA-Z])?/gi;
				const afterConjunction = /(?:e|,|\ba\s+)\s*(\d+)(?:\s*[-a-zA-Z])?/gi;
				let m;
				while ((m = mainRegex.exec(stripped)) !== null) {
					numbers.push(parseInt(m[1], 10));
				}
				while ((m = afterConjunction.exec(stripped)) !== null) {
					numbers.push(parseInt(m[1], 10));
				}
				// Remover duplicatas e ordenar (para consistência)
				return [...new Set(numbers)];
			},
			/** Verdadeiro quando a mensagem cita exatamente um artigo (uma anotação é por artigo). */
			hasSingleCitedArticle(message) {
				if (!message || message.enviado_por !== 'assistant') return false;
				const text = message.texto || message.text || message.content || '';
				const cited = this.extractAllCitedArticleNumbers(text);
				return cited.length === 1;
			},
			goToLaw(idLei, numeroLei, artigoRef) {
				if (!idLei) return;
				// Fechar o chat antes de navegar para a lei
				this.isOpen = false;
				this.emitState();
				this.$nextTick(() => {
					const slug = this.sanitizeTitle(numeroLei || '');
					const route = { path: `/painel/lei/${idLei}/${slug}` };
					if (artigoRef != null && !Number.isNaN(Number(artigoRef))) {
						route.query = { artigo: String(artigoRef) };
					}
					this.$router.push(route);
				});
			},
			/** Retorna o texto puro da mensagem (sem HTML). */
			getMessagePlainText(message) {
				if (!message) return '';
				const raw = message.texto || message.text || message.content || '';
				const div = document.createElement('div');
				div.innerHTML = raw;
				return (div.textContent || div.innerText || raw).trim();
			},
			/** Gera um documento de texto na galeria a partir da resposta da IA, nomeado com o título da conversa. */
			async generateDocumentFromMessage(message) {
				const messageKey = message.id || message.timestamp;
				const plainText = this.getMessagePlainText(message);
				if (!plainText) {
					this.snackbarChatText = 'Não há conteúdo para gerar o documento.';
					this.snackbarChatColor = 'warning';
					this.snackbarChat = true;
					return;
				}
				if (!this.activeConversation) {
					this.snackbarChatText = 'Conversa não encontrada.';
					this.snackbarChatColor = 'error';
					this.snackbarChat = true;
					return;
				}
				const titulo = this.conversationTitle(this.activeConversation) || 'Documento do Mévio';
				const userId = this.$store.state.user && this.$store.state.user.id;
				if (!userId) {
					this.snackbarChatText = 'Faça login para salvar documentos.';
					this.snackbarChatColor = 'error';
					this.snackbarChat = true;
					return;
				}
				this.generatingDocumentId = messageKey;
				try {
					const docxMime =
						'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
					const blob = new Blob([plainText], { type: docxMime });
					const fileName = `${titulo.replace(/[<>:"/\\|?*]/g, '_').slice(0, 200)}.docx`;
					const file = new File([blob], fileName, { type: docxMime });
					const formData = new FormData();
					formData.append('image', file);
					const { data: uploadData } = await this.$http.post('/files', formData, {
						headers: { 'Content-Type': 'multipart/form-data' },
					});
					const asset = uploadData.data;
					if (!asset || !asset.id) {
						throw new Error('Resposta do upload inválida');
					}
					await this.$http.post('/items/documentos', {
						id_user: userId,
						documento: asset.id,
						titulo: titulo.slice(0, 255),
						status: 'published',
						tipo: asset.type || docxMime,
						pasta: undefined,
					});
					this.snackbarChatText = 'Documento criado na galeria de documentos.';
					this.snackbarChatColor = 'success';
					this.snackbarChat = true;
					// Opcional: navegar para a tela de documentos
					this.$router.push('/painel/documentos').catch(() => {});
				} catch (err) {
					window.console.error('Erro ao gerar documento:', err);
					const errMsg = (err && err.response && err.response.data && err.response.data.errors && err.response.data.errors[0] && err.response.data.errors[0].message) || 'Erro ao gerar documento. Tente novamente.';
					this.snackbarChatText = errMsg;
					this.snackbarChatColor = 'error';
					this.snackbarChat = true;
				} finally {
					this.generatingDocumentId = null;
				}
			},
			/**
			 * Busca o id_reg (registro do artigo) na lei para poder vincular a anotação ao artigo.
			 * Retorna o id_reg do registro cujo conteudo corresponde ao número do artigo, ou null.
			 */
			async findIdRegByArticleNumber(idLei, artigoNum) {
				if (idLei == null || artigoNum == null) return null;
				const num = Number(artigoNum);
				if (Number.isNaN(num)) return null;
				try {
					const { data } = await this.$http.get(
						`items/conteudoleis/?limit=-1&sort=sort,id_reg&fields=id_reg,conteudo&filter[id_lei][_eq]=${idLei}`
					);
					const conteudos = data.data || [];
					const regex = new RegExp(
						'(?:^|[^\\d])(?:artigo|art\\.?)\\s*' + num + '(?:\\s|\\.|$|[^0-9])',
						'i'
					);
					const item = conteudos.find(function (c) {
						const text = (c.conteudo || '')
							.replace(/<[^>]+>/g, ' ')
							.replace(/\s+/g, ' ')
							.trim();
						return regex.test(text);
					});
					return item ? item.id_reg : null;
				} catch (err) {
					window.console.error('Erro ao buscar registro do artigo:', err);
					return null;
				}
			},
			/** Cria uma anotação com o texto da resposta, vinculada à lei e ao artigo citados (id_reg), e salva na tela Anotações. */
			async createAnnotationFromMessage(message) {
				const messageKey = message.id || message.timestamp;
				const plainText = this.getMessagePlainText(message);
				if (!plainText) {
					this.snackbarChatText = 'Não há conteúdo para criar a anotação.';
					this.snackbarChatColor = 'warning';
					this.snackbarChat = true;
					return;
				}
				if (!message.id_lei) {
					this.snackbarChatText = 'Esta mensagem não referencia uma lei para vincular à anotação.';
					this.snackbarChatColor = 'warning';
					this.snackbarChat = true;
					return;
				}
				const userId = this.$store.state.user && this.$store.state.user.id;
				if (!userId) {
					this.snackbarChatText = 'Faça login para criar anotações.';
					this.snackbarChatColor = 'error';
					this.snackbarChat = true;
					return;
				}
				this.creatingAnnotationId = messageKey;
				try {
					const artigoNum = message.artigo_ref != null ? message.artigo_ref : this.extractFirstArticleNumber(message.texto || message.text || message.content);
					const idReg = await this.findIdRegByArticleNumber(message.id_lei, artigoNum);
					if (idReg == null) {
						this.snackbarChatText = 'Não foi possível localizar o artigo na lei. Verifique se a lei e o artigo existem no sistema.';
						this.snackbarChatColor = 'warning';
						this.snackbarChat = true;
						return;
					}
					const payload = {
						texto: plainText,
						id_lei: message.id_lei,
						id_reg: idReg,
						status: 'private',
					};
					await this.$http.post('/items/anotacoes', payload);
					this.snackbarChatText = 'Anotação criada. Confira na tela Anotações.';
					this.snackbarChatColor = 'success';
					this.snackbarChat = true;
					this.$router
						.push({ path: '/painel/anotacoes', query: { r: String(Date.now()) } })
						.catch(function () {});
				} catch (err) {
					window.console.error('Erro ao criar anotação:', err);
					const errMsg = (err && err.response && err.response.data && err.response.data.errors && err.response.data.errors[0] && err.response.data.errors[0].message) || 'Erro ao criar anotação. Tente novamente.';
					this.snackbarChatText = errMsg;
					this.snackbarChatColor = 'error';
					this.snackbarChat = true;
				} finally {
					this.creatingAnnotationId = null;
				}
			},
			async updateConversationTitleWithAnimation(conversationId, newTitle) {
				const conversation = this.conversations.find(
					(conv) => this.getConversationId(conv) === conversationId
				);

				if (!conversation) {
					return;
				}

				const currentTitle = conversation.titulo || 'Nova conversa';
				const targetTitle = newTitle;
				let currentIndex = 0;

				if (currentTitle === targetTitle) {
					return;
				}

				const typeTitle = () => {
					if (currentIndex < targetTitle.length) {
						conversation.titulo = targetTitle.substring(0, currentIndex + 1);
						currentIndex++;
						setTimeout(typeTitle, 30);
					} else {
						conversation.titulo = targetTitle;
					}
				};

				typeTitle();
			},
			async loadConversations() {
				const userId = this.$store.state.user && this.$store.state.user.id;
				if (!userId) {
					return;
				}

				try {
					this.loadingConversations = true;

					const fields = 'id,titulo,usuario,date_created,date_updated,status';
					const { data } = await this.$http.get(
						`/items/conversas?fields=${fields}&filter[usuario][_eq]=${userId}&filter[status][_eq]=published&sort=-date_updated`
					);

					this.conversations = data.data || [];

					if (this.conversations.length > 0 && !this.selectedConversationId) {
						this.selectedConversationId = this.conversations[0].id;
						await this.loadMessages(this.conversations[0].id);
					}
				} catch (error) {
					window.console.error('Erro ao carregar conversas:', error);
					this.conversations = [];
				} finally {
					this.loadingConversations = false;
				}
			},
			async loadMessages(conversationId) {
				if (!conversationId) {
					this.messages = [];
					return;
				}

				try {
					this.loadingMessages = true;

					const fields =
						'id,texto,enviado_por,conversa,date_created,user_created,sort,lei_referenciada,lei_referenciada.id_lei,lei_referenciada.numero_lei';
						// ,arquivos.directus_files_id.id,arquivos.directus_files_id.filename_download,arquivos.directus_files_id.type';
					const { data } = await this.$http.get(
						`/items/mensagens?fields=${fields}&filter[conversa][_eq]=${conversationId}&sort=sort`
					);

					this.messages = this.messages.filter(
						(msg) => msg.conversa !== conversationId
					);

					const apiMessages = data.data || [];

					for (const message of apiMessages) {
						// Processar arquivos - COMENTADO
						// if (message.arquivos && Array.isArray(message.arquivos)) {
						// 	message.arquivos = message.arquivos
						// 		.filter(arq => arq.directus_files_id)
						// 		.map(arq => ({
						// 			id: arq.directus_files_id.id,
						// 			filename_download: arq.directus_files_id.filename_download,
						// 			type: arq.directus_files_id.type,
						// 		}));
						// }

						if (
							message.enviado_por === 'assistant' &&
							message.lei_referenciada
						) {
							try {
								let lei = null;
								const ref = message.lei_referenciada;
								// Relação expandida: API retorna objeto { id_lei, numero_lei }
								if (ref && typeof ref === 'object' && (ref.id_lei != null || ref.id != null)) {
									lei = {
										id_lei: ref.id_lei != null ? ref.id_lei : ref.id,
										numero_lei: ref.numero_lei || '',
									};
								}
								// ID da lei (FK)
								if (!lei && typeof ref === 'number') {
									const { data: leisData } = await this.$http.get(
										`/items/leis?fields=id_lei,numero_lei&filter[id_lei][_eq]=${ref}&limit=1`
									);
									if (leisData.data && leisData.data.length > 0) lei = leisData.data[0];
								}
								// String (ex.: numero_lei "CPC")
								if (!lei && typeof ref === 'string') {
									const { data: leisData } = await this.$http.get(
										`/items/leis?fields=id_lei,numero_lei&filter[numero_lei][_eq]=${encodeURIComponent(
											ref
										)}&limit=1`
									);
									if (leisData.data && leisData.data.length > 0) lei = leisData.data[0];
								}
								if (lei) {
									this.$set(message, 'id_lei', lei.id_lei);
									this.$set(message, 'numero_lei', lei.numero_lei);
									const artigoRef = this.extractFirstArticleNumber(message.texto);
									if (artigoRef != null) this.$set(message, 'artigo_ref', artigoRef);
								}
							} catch (error) {
								window.console.error('Erro ao buscar lei referenciada:', error);
							}
						}
					}

					this.messages.push(...apiMessages);

					this.$nextTick(() => this.scrollThreadToBottom());
				} catch (error) {
					window.console.error('Erro ao carregar mensagens:', error);
				} finally {
					this.loadingMessages = false;
				}
			},
			async createNewConversation() {
				const userId = this.$store.state.user && this.$store.state.user.id;
				if (!userId) {
					return;
				}

				try {
					this.loadingConversations = true;

					const { data } = await this.$http.post('/items/conversas', {
						titulo: 'Nova conversa',
						usuario: userId,
						status: 'published',
					});

					if (data.data && data.data.id) {
						this.conversations.unshift(data.data);

						this.selectedConversationId = data.data.id;

						this.messages = this.messages.filter(
							(msg) => msg.conversa !== data.data.id
						);
					}
				} catch (error) {
					window.console.error('Erro ao criar conversa:', error);
				} finally {
					this.loadingConversations = false;
				}
			},
			async deleteConversation(conversation) {
				const conversationId = this.getConversationId(conversation);
				if (!conversationId) {
					return;
				}

				if (!confirm('Tem certeza que deseja deletar esta conversa?')) {
					return;
				}

				try {
					await this.$http.patch(`/items/conversas/${conversationId}`, {
						status: 'archived',
					});

					this.conversations = this.conversations.filter(
						(conv) => this.getConversationId(conv) !== conversationId
					);

					if (this.selectedConversationId === conversationId) {
						this.selectedConversationId = null;
						this.messages = this.messages.filter(
							(msg) => msg.conversa !== conversationId
						);

						if (this.conversations.length > 0) {
							this.selectedConversationId = this.conversations[0].id;
							await this.loadMessages(this.conversations[0].id);
						}
					}
				} catch (error) {
					window.console.error('Erro ao deletar conversa:', error);
					alert('Erro ao deletar conversa. Tente novamente.');
				}
			},
			formatTime(dateString) {
				if (!dateString) {
					return '';
				}

				const date = new Date(dateString);
				const now = new Date();
				const diffMs = now - date;
				const diffMins = Math.floor(diffMs / 60000);
				const diffHours = Math.floor(diffMs / 3600000);
				const diffDays = Math.floor(diffMs / 86400000);

				if (diffMins < 1) {
					return 'Agora';
				} else if (diffMins < 60) {
					return `${diffMins}min`;
				} else if (diffHours < 24) {
					return `${diffHours}h`;
				} else if (diffDays === 1) {
					return 'Ontem';
				} else if (diffDays < 7) {
					return `${diffDays}d`;
				} else {
					return date.toLocaleDateString('pt-BR', {
						day: '2-digit',
						month: '2-digit',
					});
				}
			},
			formatRelativeTime(dateString) {
				if (!dateString) {
					return '';
				}

				const date = new Date(dateString);
				const now = new Date();
				const diffMs = now - date;
				const diffMins = Math.floor(diffMs / 60000);
				const diffHours = Math.floor(diffMs / 3600000);
				const diffDays = Math.floor(diffMs / 86400000);

				if (diffMins < 1) {
					return 'Agora';
				} else if (diffMins < 60) {
					return `${diffMins}min`;
				} else if (diffHours < 24) {
					return `${diffHours}h`;
				} else if (diffDays === 1) {
					return 'Ontem';
				} else if (diffDays < 7) {
					return `${diffDays}d`;
				} else {
					return date.toLocaleDateString('pt-BR', {
						day: '2-digit',
						month: '2-digit',
					});
				}
			},
			handleResize() {
				// Ajustar scroll quando o teclado aparecer/desaparecer
				this.$nextTick(() => {
					this.scrollThreadToBottom();
				});
			},
			handleInputFocus() {
				// Garantir que o input está acessível
				this.$nextTick(() => {
					if (this.$refs.messageInput && this.$refs.messageInput.$el) {
						const textarea = this.$refs.messageInput.$el.querySelector('textarea');
						if (textarea) {
							textarea.focus();
						}
					}
				});
			},
			handleEnterKey(event) {
				// No mobile, Enter sempre cria nova linha (comportamento padrão)
				if (this.isMobile) {
					return; // Permite o comportamento padrão (nova linha)
				}
				// No desktop, Enter envia a mensagem
				event.preventDefault();
				this.submitMessage();
			},
			startResize(event) {
				if (this.isMobile) return;
				this.isResizing = true;
				this.startX = event.clientX;
				this.startWidth = this.conversationsWidth;
				document.addEventListener('mousemove', this.handleResize);
				document.addEventListener('mouseup', this.stopResize);
				event.preventDefault();
				event.stopPropagation();
			},
			handleResize(event) {
				if (!this.isResizing || this.isMobile) return;
				
				const deltaX = event.clientX - this.startX;
				const newWidth = this.startWidth + deltaX;
				
				// Limites: mínimo 240px, máximo 500px
				const minWidth = 240;
				const maxWidth = 500;
				
				if (newWidth >= minWidth && newWidth <= maxWidth) {
					this.conversationsWidth = Math.round(newWidth);
					// Salvar no localStorage
					localStorage.setItem('chat-conversations-width', this.conversationsWidth.toString());
				}
			},
			stopResize() {
				this.isResizing = false;
				document.removeEventListener('mousemove', this.handleResize);
				document.removeEventListener('mouseup', this.stopResize);
			},
			// Métodos de anexo - COMENTADOS
			// handleFileSelect(event) {
			// 	const files = Array.from(event.target.files || []);
			// 	if (files.length === 0) return;

			// 	// Adicionar arquivos à lista
			// 	files.forEach(file => {
			// 		// Verificar tamanho (limite de 10MB por arquivo)
			// 		if (file.size > 10 * 1024 * 1024) {
			// 			alert(`O arquivo "${file.name}" é muito grande. Tamanho máximo: 10MB`);
			// 			return;
			// 		}
			// 		this.attachedFiles.push(file);
			// 	});

			// 	// Limpar input
			// 	if (this.$refs.fileInput) {
			// 		this.$refs.fileInput.value = '';
			// 	}
			// },
			// removeAttachment(index) {
			// 	this.attachedFiles.splice(index, 1);
			// 	// Garantir que o input receba foco após remover anexo
			// 	this.$nextTick(() => {
			// 		if (this.$refs.messageInput && this.$refs.messageInput.$el) {
			// 			const textarea = this.$refs.messageInput.$el.querySelector('textarea');
			// 			if (textarea) {
			// 				textarea.focus();
			// 			}
			// 		}
			// 	});
			// },
			// async uploadFile(file) {
			// 	const formData = new FormData();
			// 	formData.append('file', file);

			// 	try {
			// 		const { data } = await this.$http.post('/files', formData, {
			// 			headers: {
			// 				'Content-Type': 'multipart/form-data',
			// 			},
			// 		});
			// 		return data.data;
			// 	} catch (error) {
			// 		window.console.error('Erro ao fazer upload do arquivo:', error);
			// 		throw error;
			// 	}
			// },
			// getFileIcon(filename) {
			// 	if (!filename) return 'mdi-file';
				
			// 	const ext = filename.split('.').pop().toLowerCase();
			// 	const iconMap = {
			// 		// Documentos
			// 		pdf: 'mdi-file-pdf-box',
			// 		doc: 'mdi-file-word-box',
			// 		docx: 'mdi-file-word-box',
			// 		xls: 'mdi-file-excel-box',
			// 		xlsx: 'mdi-file-excel-box',
			// 		ppt: 'mdi-file-powerpoint-box',
			// 		pptx: 'mdi-file-powerpoint-box',
			// 		// Imagens
			// 		jpg: 'mdi-file-image',
			// 		jpeg: 'mdi-file-image',
			// 		png: 'mdi-file-image',
			// 		gif: 'mdi-file-image',
			// 		svg: 'mdi-file-image',
			// 		// Arquivos
			// 		zip: 'mdi-folder-zip',
			// 		rar: 'mdi-folder-zip',
			// 		txt: 'mdi-file-document-outline',
			// 		// Vídeo
			// 		mp4: 'mdi-file-video',
			// 		avi: 'mdi-file-video',
			// 		mov: 'mdi-file-video',
			// 		// Áudio
			// 		mp3: 'mdi-file-music',
			// 		wav: 'mdi-file-music',
			// 		ogg: 'mdi-file-music',
			// 	};
			// 	return iconMap[ext] || 'mdi-file';
			// },
			// getFileUrl(arquivo) {
			// 	if (arquivo.id) {
			// 		return `https://api.mylegis.com.br/assets/${arquivo.id}?download`;
			// 	}
			// 	return '#';
			// },
		},
		async mounted() {
			console.log('[Mévio tooltip] FloatingChat mounted', { shouldShowChat: this.shouldShowChat });
			if (this.shouldShowChat) {
				await this.loadConversations();
				// Tooltip automático do Mévio: exibe uma vez e só reaparece após 10 dias
				console.log('[Mévio tooltip] mounted com chat visível, agendando scheduleMeioTooltip em 800ms');
				setTimeout(() => this.scheduleMeioTooltip(), 800);
			} else {
				console.log('[Mévio tooltip] mounted sem chat visível (shouldShowChat=false); tooltip será agendado quando entrar no painel');
			}
			// Adicionar listener para ajustar scroll quando teclado aparecer (mobile)
			if (this.isMobile) {
				window.addEventListener('resize', this.handleResize);
			}
		},
		beforeDestroy() {
			this.dismissMeioTooltip();
			if (this.isMobile) {
				window.removeEventListener('resize', this.handleResize);
			}
			// Limpar listeners de redimensionamento
			this.stopResize();
		},
	};
</script>

<style scoped>
	.floating-chat {
		position: fixed;
		right: 24px;
		bottom: 24px;
		z-index: 2100;
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: 16px;
		max-width: calc(100vw - 48px);
	}

	.chat-container {
		width: 1000px;
		max-width: calc(100vw - 48px) !important;
		max-height: calc(90vh - 48px) !important;
		display: flex;
		flex-direction: column;
		border-radius: 16px;
		overflow: hidden;
		box-sizing: border-box;
	}

	.chat-container ::v-deep .v-card__text {
		overflow: visible;
	}

	.chat-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: 5px 20px;
		position: relative;
	}

	.chat-mobile-conversations-toggle {
		position: absolute;
		left: 16px;
		top: 50%;
		transform: translateY(-50%);
		z-index: 10;
		flex-shrink: 0;
	}

	.chat-header__actions {
		display: flex;
		gap: 8px;
	}

	.chat-header__label {
		font-size: 0.75rem;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: #8f8f8f;
	}

	.chat-header__title {
		flex: 1;
		margin-left: 0;
		min-width: 0;
		overflow: hidden;
	}

	.chat-header__title:has(~ .chat-header__actions) {
		margin-right: 8px;
	}

	.chat-header__headline {
		margin: 4px 0 0;
		font-size: 1.1rem;
		color: #1f1f1f;
	}

	.chat-header__title:has(~ .chat-header__actions) {
		margin-right: 8px;
	}

	.chat-body {
		flex: 1;
		padding: 16px;
		background: linear-gradient(
			135deg,
			rgba(241, 90, 36, 0.05),
			rgba(241, 90, 36, 0.03)
		);
		display: flex;
		gap: 0;
		align-items: stretch;
		min-height: 0;
		box-sizing: border-box;
		max-height:73vh;
		position: relative;
		overflow: hidden;
	}
	
	.chat-resizer {
		width: 4px;
		background-color: transparent;
		cursor: col-resize;
		position: relative;
		flex-shrink: 0;
		z-index: 10;
		margin: 0 8px;
		transition: background-color 0.2s ease;
	}
	
	.chat-resizer:hover {
		background-color: rgba(241, 90, 36, 0.3);
	}
	
	.chat-resizer::before {
		content: '';
		position: absolute;
		left: -2px;
		right: -2px;
		top: 0;
		bottom: 0;
		cursor: col-resize;
	}
	
	.chat-body.resizing {
		user-select: none;
		cursor: col-resize;
	}
	
	.chat-body.resizing .chat-resizer {
		background-color: rgba(241, 90, 36, 0.5);
	}

	.floating-button {
		box-shadow: 0 12px 24px rgba(241, 90, 36, 0.3);
	}

	.chat-fade-enter-active,
	.chat-fade-leave-active {
		transition: all 0.2s ease-out;
	}

	.chat-fade-enter,
	.chat-fade-leave-to {
		opacity: 0;
		transform: translateY(12px);
	}

	.chat-conversations {
		flex: 0 0 auto;
		display: flex;
		flex-direction: column;
		background-color: #ffffff;
		border-radius: 12px;
		padding: 16px;
		padding-top: 20px;
		box-shadow: inset 0 0 0 1px rgba(241, 90, 36, 0.1);
		min-height: 0;
		box-sizing: border-box;
		position: relative;
		z-index: 1;
		overflow: hidden;
		transition: width 0.1s ease-out;
	}

	.chat-conversations__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 8px;
		padding-top: 4px;
		gap: 12px;
		flex-wrap: nowrap;
		position: relative;
		min-height: 40px;
	}

	.chat-conversations__title {
		font-size: 1rem;
		font-weight: 600;
		margin: 0;
		padding-top: 2px;
		line-height: 1.4;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		flex: 1;
		min-width: 0;
	}

	.chat-conversations__header-actions {
		display: flex;
		align-items: center;
		gap: 8px;
		flex-shrink: 0;
	}

	.chat-conversations__header h4 {
		font-size: 1rem;
		font-weight: 600;
		margin: 0;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.chat-conversations__new {
		text-transform: none;
		font-weight: 500;
		color: #f15a24;
		padding: 0 8px;
		min-width: auto;
		white-space: nowrap;
		flex-shrink: 0;
		margin: 0;
	}

	.chat-conversations__close-mobile {
		flex-shrink: 0;
		margin: 0;
		position: relative;
	}

	.chat-conversations__new-text {
		display: inline;
	}

	.chat-conversations__list {
		flex: 1;
		overflow-y: auto;
		overflow-x: hidden;
		padding-right: 4px;
		min-height: 0;
		-webkit-overflow-scrolling: touch;
		position: relative;
		z-index: 1;
	}

	.chat-conversations__list::-webkit-scrollbar {
		width: 6px;
	}

	.chat-conversations__list::-webkit-scrollbar-thumb {
		border-radius: 6px;
		background: rgba(241, 90, 36, 0.3);
	}

	.chat-conversations__items {
		background: transparent;
		min-height: 100%;
		position: relative;
		z-index: 1;
	}

	.conversation-item {
		border-radius: 12px;
		margin-bottom: 6px;
		transition: background-color 0.2s ease;
		padding: 8px 4px !important;
		cursor: pointer;
		position: relative;
		z-index: 1;
	}

	.conversation-item:active {
		background-color: rgba(241, 90, 36, 0.2);
		transition: background-color 0.1s ease;
	}

	.conversation-item .v-list-item__action,
	.conversation-item__action {
		position: relative;
		z-index: 10;
		flex-shrink: 0;
	}

	.conversation-delete-btn {
		position: relative;
		z-index: 11;
	}

	.conversation-item--active {
		background: rgba(241, 90, 36, 0.12);
	}

	.conversation-avatar {
		background: rgba(241, 90, 36, 0.12);
		color: #f15a24;
		font-weight: 600;
		font-size: 1.1rem !important;
	}

	.conversation-avatar span {
		font-size: 1.1rem;
		font-weight: 600;
	}

	.conversation-item .v-list-item__title {
		font-size: 0.95rem !important;
		font-weight: 500 !important;
		line-height: 1.4 !important;
		margin-bottom: 2px;
	}
	
	/* Ajustar z-index do tooltip para aparecer acima do chat */
	.conversation-item ::v-deep .v-tooltip__content {
		z-index: 99999999 !important;
	}

	.conversation-item .v-list-item__subtitle {
		font-size: 0.85rem !important;
		line-height: 1.3 !important;
		opacity: 0.8;
	}

	.conversation-subtitle-row {
		display: flex;
		justify-content: space-between;
		align-items: center;
		gap: 8px;
		margin-top: 2px;
	}

	.conversation-subtitle-text {
		flex: 1;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		min-width: 0;
	}

	.conversation-last-activity {
		font-size: 0.75rem !important;
		color: #8f8f8f;
		flex-shrink: 0;
		white-space: nowrap;
	}

	.conversation-delete-btn {
		margin-left: 8px;
		position: relative;
		z-index: 11;
	}

	.chat-conversations__empty {
		margin-top: 24px;
		text-align: center;
		color: #6f6f6f;
		font-size: 0.9rem;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 12px;
	}

	.chat-thread {
		flex: 1;
		min-width: 0;
		min-height: 0;
		display: flex;
		flex-direction: column;
		background-color: rgba(255, 255, 255, 0.92);
		border-radius: 12px;
		padding: 12px 16px 16px;
		box-shadow: inset 0 0 0 1px rgba(241, 90, 36, 0.1);
		box-sizing: border-box;
		overflow: hidden;
		position: relative;
		z-index: 0;
	}

	.chat-thread__header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 12px;
	}

	.chat-thread__header h4 {
		margin: 0;
		padding-top: 2px;
		font-size: 1rem;
		font-weight: 600;
		line-height: 1.4;
	}

	.chat-thread__header small {
		color: #6f6f6f;
	}

	.chat-thread__messages {
		flex: 1;
		overflow-y: auto;
		overflow-x: hidden;
		padding-right: 6px;
		display: flex;
		flex-direction: column;
		gap: 12px;
		min-height: 180px;
		min-width: 0;
		-webkit-overflow-scrolling: touch;
		position: relative;
		z-index: 0;
	}

	.chat-thread__messages::-webkit-scrollbar {
		width: 6px;
	}

	.chat-thread__messages::-webkit-scrollbar-thumb {
		border-radius: 6px;
		background: rgba(241, 90, 36, 0.3);
	}

	.chat-thread__placeholder,
	.chat-thread__empty {
		display: flex;
		flex-direction: column;
		align-items: center;
		text-align: center;
		justify-content: center;
		margin-top: 40px;
		color: #6f6f6f;
		gap: 12px;
	}

	.message-bubble {
		align-self: flex-start;
		background: rgba(241, 90, 36, 0.09);
		border-radius: 16px 16px 16px 4px;
		padding: 12px 16px;
		max-width: 82%;
		line-height: 1.4;
		word-wrap: break-word;
		overflow-wrap: break-word;
		position: relative;
		z-index: 0;
	}

	.message-bubble--outgoing {
		align-self: flex-end;
		background: #f15a24;
		color: white;
		border-radius: 16px 16px 4px 16px;
	}

	.message-bubble--outgoing .message-bubble__time {
		color: rgba(255, 255, 255, 0.9);
	}

	.message-bubble__content {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.message-bubble__text {
		margin: 0;
		font-size: 0.95rem;
		word-break: break-word;
	}

	.message-bubble__footer {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 8px;
		margin-top: 4px;
	}

	.message-bubble__time {
		font-size: 0.75rem;
		opacity: 0.7;
	}

	.message-bubble__menu-btn {
		margin: -4px -4px -4px 0;
		opacity: 0.75;
	}

	.message-bubble__menu-btn:hover {
		opacity: 1;
	}

	.message-bubble__law-actions {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		align-self: flex-start;
	}

	.message-bubble__law-button {
		align-self: flex-start;
		text-transform: none;
	}

	/* Links gerados via v-html não recebem o atributo do scoped,
	   então precisamos usar ::v-deep para o CSS alcançar o <a>. */
	.chat-thread__messages ::v-deep .message-link,
	.chat-thread__messages ::v-deep .message-link:visited {
		color: inherit !important;
		text-decoration: underline;
	}

	/* No balão de saída (laranja), forçar links em branco (não azul). */
	.message-bubble--outgoing ::v-deep .message-link,
	.message-bubble--outgoing ::v-deep .message-link:visited {
		color: #fff !important;
	}

	.message-bubble--typing {
		align-self: flex-start;
		background: rgba(241, 90, 36, 0.09);
		border-radius: 16px 16px 16px 4px;
		padding: 16px 20px;
		max-width: 82px;
	}

	.typing-indicator {
		display: flex;
		align-items: center;
		gap: 4px;
		padding: 4px 0;
	}

	.typing-indicator span {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		background-color: rgba(241, 90, 36, 0.6);
		display: inline-block;
		animation: typing-bounce 1.4s infinite ease-in-out;
	}

	.typing-indicator span:nth-child(1) {
		animation-delay: -0.32s;
	}

	.typing-indicator span:nth-child(2) {
		animation-delay: -0.16s;
	}

	@keyframes typing-bounce {
		0%,
		80%,
		100% {
			transform: scale(0.8);
			opacity: 0.5;
		}
		40% {
			transform: scale(1);
			opacity: 1;
		}
	}

	.chat-conversations__list .v-skeleton-loader {
		margin-bottom: 8px;
		border-radius: 12px;
	}

	.chat-thread__messages .v-skeleton-loader {
		margin-bottom: 12px;
		border-radius: 16px;
	}

	.message-bubble--loading {
		align-self: flex-start;
		background: rgba(241, 90, 36, 0.05);
		border-radius: 16px 16px 16px 4px;
		padding: 12px 16px;
		max-width: 70%;
		animation: pulse-loading 1.5s ease-in-out infinite;
	}

	.conversation-item--loading {
		padding: 8px 4px;
		animation: pulse-loading 1.5s ease-in-out infinite;
	}

	@keyframes pulse-loading {
		0%,
		100% {
			opacity: 1;
		}
		50% {
			opacity: 0.6;
		}
	}

	.chat-thread__footer {
		display: flex;
		flex-direction: column;
		gap: 8px;
		margin-top: 16px;
		flex-shrink: 0;
		width: 100%;
		position: relative;
		z-index: 10;
		background: transparent;
	}

	.chat-thread__input {
		flex: 1;
		min-width: 0;
		position: relative;
		z-index: 1;
		transition: all 0.2s ease;
	}

	/* Estilos de anexo - COMENTADOS */
	/* .chat-thread__input--with-attachments ::v-deep .v-input__control {
		border-color: rgba(241, 90, 36, 0.4) !important;
	}

	.chat-thread__input--with-attachments ::v-deep .v-text-field--outlined fieldset {
		border-color: rgba(241, 90, 36, 0.4) !important;
		border-width: 2px !important;
	} */

	.chat-thread__input ::v-deep .v-text-field__slot {
		align-items: flex-end;
	}

	.chat-thread__input ::v-deep textarea {
		resize: none;
		overflow-y: auto;
		max-height: 120px;
		pointer-events: auto;
	}

	.chat-thread__input ::v-deep .v-input__control {
		pointer-events: auto;
	}

	.chat-thread__send {
		box-shadow: none;
	}

	.chat-thread__input-wrapper {
		display: flex;
		align-items: flex-end;
		gap: 8px;
		width: 100%;
		flex-shrink: 0;
		position: relative;
		z-index: 10;
	}

	/* Estilos de botão de anexo - COMENTADOS */
	/* .chat-thread__attach {
		flex-shrink: 0;
		box-shadow: none;
		position: relative;
		transition: all 0.2s ease;
	}

	.chat-thread__attach--active {
		background-color: rgba(241, 90, 36, 0.1) !important;
	}

	.chat-thread__attach-badge {
		position: absolute;
		top: 4px;
		right: 4px;
	} */

	/* Estilos de preview de anexos - COMENTADOS */
	/* .chat-thread__attachments-preview {
		display: flex;
		flex-direction: column;
		gap: 8px;
		width: 100%;
		padding: 12px;
		background: rgba(241, 90, 36, 0.08);
		border: 2px solid rgba(241, 90, 36, 0.2);
		border-radius: 8px;
		flex-shrink: 0;
		box-shadow: 0 2px 4px rgba(241, 90, 36, 0.1);
	}

	.chat-thread__attachments-header {
		display: flex;
		align-items: center;
		font-size: 0.85rem;
		font-weight: 600;
		color: #f15a24;
		margin-bottom: 4px;
	}

	.chat-thread__attachments-title {
		font-weight: 600;
	}

	.chat-thread__attachments-list {
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.attachment-preview {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 12px;
		background: white;
		border: 1px solid rgba(241, 90, 36, 0.2);
		border-radius: 6px;
		font-size: 0.85rem;
		pointer-events: auto;
		transition: all 0.2s ease;
	}

	.attachment-preview:hover {
		background: rgba(241, 90, 36, 0.05);
		border-color: rgba(241, 90, 36, 0.4);
	}

	.attachment-preview__icon {
		color: #f15a24;
		flex-shrink: 0;
	}

	.attachment-preview__name {
		flex: 1;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		color: #1f1f1f;
	}

	.attachment-preview__remove {
		flex-shrink: 0;
		color: #8f8f8f;
	} */

	/* Estilos de anexos nas mensagens - COMENTADOS */
	/* .message-bubble__attachments {
		display: flex;
		flex-direction: column;
		gap: 8px;
		margin-top: 8px;
	}

	.message-attachment {
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 8px 12px;
		background: rgba(255, 255, 255, 0.3);
		border-radius: 8px;
		transition: background-color 0.2s;
	}

	.message-bubble--outgoing .message-attachment {
		background: rgba(255, 255, 255, 0.2);
	}

	.message-attachment:hover {
		background: rgba(255, 255, 255, 0.4);
	}

	.message-bubble--outgoing .message-attachment:hover {
		background: rgba(255, 255, 255, 0.3);
	}

	.message-attachment__icon {
		flex-shrink: 0;
		color: inherit;
	}

	.message-attachment__link {
		flex: 1;
		color: inherit;
		text-decoration: none;
		font-size: 0.9rem;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.message-attachment__link:hover {
		text-decoration: underline;
	} */

	/* Mobile optimizations */
	@media (max-width: 960px) {
		.floating-chat {
			right: 16px;
			bottom: 16px;
			left: auto;
			top: auto;
			align-items: flex-end;
			gap: 16px;
		}

		.floating-chat--open {
			right: 0 !important;
			bottom: 0 !important;
			left: 0 !important;
			top: 0 !important;
			width: 100vw !important;
			height: 100vh !important;
			max-width: 100vw !important;
			max-height: 100vh !important;
			align-items: stretch;
			gap: 0;
			position: fixed !important;
		}

		.floating-chat--open .floating-button {
			display: none;
		}

		.floating-button {
			position: relative;
		}

		.floating-chat--open .chat-container {
			width: 100% !important;
			height: 100% !important;
			max-width: 100% !important;
			max-height: 100% !important;
			border-radius: 0 !important;
			display: flex;
			flex-direction: column;
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
		}

		.chat-header {
			padding: 12px 16px;
			flex-shrink: 0;
			position: relative;
		}

		.chat-header__headline {
			font-size: 1rem;
		}

		.chat-header__label {
			font-size: 0.7rem;
		}

		.chat-header__title {
			margin-left: 40px;
		}

		.chat-mobile-conversations-toggle {
			position: absolute;
			left: 12px;
			top: 50%;
			transform: translateY(-50%);
			z-index: 10;
			flex-shrink: 0;
		}

		.chat-header__title {
			margin-left: 40px;
			flex: 1;
			min-width: 0;
		}

		.chat-body {
			flex: 1;
			min-height: 0;
			padding: 12px;
			max-height: none;
			position: relative;
		}

		.chat-mobile-overlay {
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background: rgba(0, 0, 0, 0.5);
			z-index: 1;
			animation: fadeIn 0.2s ease-out;
		}

		.chat-conversations {
			position: fixed;
			top: 0;
			left: 0;
			bottom: 0;
			width: 85%;
			max-width: 320px;
			z-index: 2;
			transform: translateX(-100%);
			transition: transform 0.3s ease-out;
			box-shadow: 2px 0 12px rgba(0, 0, 0, 0.15);
			border-radius: 0;
		}

		.chat-conversations--mobile-open {
			transform: translateX(0);
		}

		.chat-conversations__header {
			padding-top: 50px;
			position: relative;
			gap: 8px;
		}

		.chat-conversations__title {
			flex: 1;
			min-width: 0;
		}

		.chat-conversations__header-actions {
			gap: 4px;
		}

		.chat-conversations__close-mobile {
			position: relative;
			flex-shrink: 0;
		}

		.chat-conversations__new-text {
			display: none;
		}

		.chat-conversations__new {
			padding: 0 4px;
		}

		.chat-conversations__new .v-icon {
			margin: 0 !important;
		}

		.chat-thread {
			width: 100%;
			flex: 1;
			min-height: 0;
			display: flex;
			flex-direction: column;
			padding: 8px 12px 12px;
			position: relative;
			z-index: 0;
			overflow: hidden;
		}

		.chat-thread__header {
			margin-bottom: 8px;
			padding-bottom: 8px;
			border-bottom: 1px solid rgba(241, 90, 36, 0.1);
		}

		.chat-thread__header h4 {
			font-size: 0.95rem;
		}

		.chat-thread__header small {
			font-size: 0.8rem;
		}

		.chat-thread__messages {
			flex: 1;
			min-height: 0;
			padding-right: 4px;
			gap: 10px;
		}

		.message-bubble {
			max-width: 85%;
			padding: 10px 14px;
			font-size: 0.9rem;
		}

		.message-bubble__text {
			font-size: 0.9rem;
			line-height: 1.5;
		}

		.message-bubble__time {
			font-size: 0.7rem;
			margin-top: 4px;
		}

		.chat-thread__footer {
			margin-top: 12px;
			padding-top: 12px;
			border-top: 1px solid rgba(241, 90, 36, 0.1);
			flex-shrink: 0;
			gap: 8px;
			flex-direction: column;
			width: 100%;
		}

		.chat-thread__input-wrapper {
			width: 100%;
		}

		/* Estilos mobile de anexos - COMENTADOS */
		/* .chat-thread__attachments-preview {
			width: 100%;
			padding: 10px;
		}

		.chat-thread__attachments-header {
			font-size: 0.8rem;
		}

		.attachment-preview {
			padding: 6px 10px;
			font-size: 0.8rem;
		}

		.chat-thread__attach--active {
			background-color: rgba(241, 90, 36, 0.15) !important;
		} */

		.chat-thread__input {
			flex: 1;
		}

		.chat-thread__input ::v-deep .v-input__control {
			min-height: 44px;
		}

		.chat-thread__input ::v-deep .v-text-field__details {
			display: none;
		}

		.chat-thread__send {
			flex-shrink: 0;
		}

		/* Safe area para dispositivos com notch */
		@supports (padding: max(0px)) {
			.floating-chat {
				padding-bottom: max(0px, env(safe-area-inset-bottom));
			}

			.chat-thread__footer {
				padding-bottom: max(12px, env(safe-area-inset-bottom));
			}
		}
	}

	@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}

	/* Ajustes para telas muito pequenas */
	@media (max-width: 360px) {
		.chat-conversations {
			width: 90%;
		}

		.message-bubble {
			max-width: 90%;
			padding: 8px 12px;
		}
	}

	/* Melhorias gerais para mobile */
	@media (max-width: 960px) and (orientation: portrait) {
		.chat-container {
			height: 100vh;
			height: -webkit-fill-available;
		}
	}
</style>

<style>
/* Tooltip do Mévio: estilos globais (conteúdo é renderizado em portal) */
.floating-chat-meio-tooltip {
	max-width: 280px;
	padding: 12px 16px !important;
	background: linear-gradient(135deg, #363636 0%, #2d2d2d 100%) !important;
	color: #fff !important;
	border-radius: 12px !important;
	box-shadow:
		0 10px 40px rgba(0, 0, 0, 0.25),
		0 0 0 1px rgba(241, 90, 36, 0.25),
		0 4px 0 0 #F15A24 !important;
	font-family: inherit !important;
	line-height: 1.4 !important;
	border: none !important;
}

.floating-chat-meio-tooltip .meio-tooltip__content {
	display: flex;
	align-items: center;
	gap: 10px;
}

.floating-chat-meio-tooltip .meio-tooltip__icon {
	color: #F15A24 !important;
	flex-shrink: 0;
}

.floating-chat-meio-tooltip .meio-tooltip__text {
	font-size: 0.9375rem;
	font-weight: 500;
	letter-spacing: 0.01em;
	color: #fff !important;
}

/* Animação de entrada/saída suave */
.meio-tooltip-transition-enter-active,
.meio-tooltip-transition-leave-active {
	transition: opacity 0.25s ease, transform 0.25s ease;
}

.meio-tooltip-transition-enter,
.meio-tooltip-transition-leave-to {
	opacity: 0;
	transform: translateY(8px);
}
</style>
